import { Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, OnDestroy, HostListener } from '@angular/core'
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepicker, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { formatISO, startOfTomorrow } from 'date-fns'
import { FormBuilder, Validators } from '@angular/forms';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core'
import { LocalDate } from '@js-joda/core'
import { Subject, Observable, of, BehaviorSubject } from 'rxjs'
import { takeUntil, filter, debounceTime, distinctUntilChanged, tap, switchMap, shareReplay, startWith, take } from 'rxjs/operators'

import { AlertService } from '@/services/alert.service'
import { AuthenticationService } from '@/services/authentication.service'
import { RacksService } from '@/services/racks.service'
import { SalesPeriodsService } from '@/services/sales-periods.service'
import { SellersService } from '@/services/sellers.service'
import { ServiceProductsService } from '@/services/service-products.service'
import { StoresService } from '@/services/stores.service'

import { Rack } from '@/domain/rack'
import { SalesPeriod } from '@/domain/sales-period'
import { Seller } from '@/domain/seller'
import { ServiceProduct } from '@/domain/service-product'
import { ServiceProductVariation } from '@/domain/service-product-variation'
import { Store } from '@/domain/store'

import { SelectedVariationAndDate } from '@/model/calendar'
import { CustomPeriodStatus } from '@/domain/custom-period-status';

@UntilDestroy()
@Component({
  selector: 'sales-period-edit-modal',
  templateUrl: 'sales-period-edit-modal.component.html',
  styleUrls: [ 'sales-period-edit-modal.component.scss' ],
})
export class SalesPeriodEditModalComponent implements OnInit {

  /** Sales period to edit */
  @Input()
  set period(period: SalesPeriod) {
    this._updateFormFromPeriod(period)
  }
  get period(): SalesPeriod {
    return this.editingPeriod
  }

  /** Emitted on update of sales period */
  @Output()
  periodChange = new EventEmitter<SalesPeriod>()

  /** Emitted on creation of sales period */
  @Output()
  created = new EventEmitter<SalesPeriod>()

  /** Set to true to pop up the modal */
  @Input()
  set open(open: any) {
    this._open = open
    this.onChange()
  }

  /** Selections to preselect for sales period creation */
  @Input()
  set selection(selection: SelectedVariationAndDate) {
    this.editingPeriod = null
    this._selectionData = selection
    this._updateFormFromSelection(selection)
    // this._setStoreFromSelection(selection)
  }

  /** Whether to provide a link button to the period. Default false */
  @Input()
  set link(whether: boolean) {
    this.linkToPeriod = whether
  }

  @Output()
  closed = new EventEmitter<boolean>()

  @ViewChild('salesPeriodModalTemplate')
  private modalTemplate: TemplateRef<any>

  @ContentChild('datepicker')
  datepicker: NgbDatepicker

  linkToPeriod: boolean = false
  editingPeriod: SalesPeriod = null
  availabilityOfSelectedRack: LocalDate[] = []

  datePickerOpen = false
  submitting = false

  stores: Store[] = null
  serviceProducts: ServiceProduct[] = null

  racks: Rack[] = null
  racksByStoreId = {}

  serviceProductsForSelectedStore: ServiceProduct[] = null
  racksForSelectedStore: Rack[] = null
  racksForSelectedStoreAndProduct: Rack[] = null

  // Async seller search properties
  sellers$ = new BehaviorSubject<Seller[]>([]) // Use BehaviorSubject for manual control
  sellersLoading = false
  sellersInput$ = new Subject<string>()
  minSearchLength = 2

  private _open: boolean = false
  private _modal: NgbModalRef = null
  private _selectionData: SelectedVariationAndDate = null
  // private _selectedRackId: number = null

  salesPeriodForm = this.fb.group({
    store_id: [''],
    service_product_id: ['', Validators.required],
    service_product_variation_id: ['', Validators.required],
    rack_id: ['', Validators.required],
    rack_time_slot_id: [''],
    from: ['', Validators.required],
    to: ['', Validators.required],
    price: ['', Validators.required],
    commission: ['', Validators.required],
    max_items: [''],
    discount: [''],
    seller_id: ['', Validators.required],
    seller_note: [''],
    internal_note: [''],
    payment_status: ['payment_status_none'],
    custom_period_status_id: ['']
  })

  constructor(
    public auth: AuthenticationService,
    public sellersService: SellersService,
    private salesPeriodsService: SalesPeriodsService,
    private serviceProductService: ServiceProductsService,
    private storesService: StoresService,
    private racksService: RacksService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private translate: TranslateService,
    private calendar: NgbCalendar,
    private formatter: NgbDateParserFormatter,
    private fb: FormBuilder
  ) {
    this.racksService.racks.pipe(untilDestroyed(this)).subscribe(racks => {
      if (racks.length == 0) { return }
      this.racks = racks
      this.racksByStoreId = this.racks.reduce((rv, val) => {
        (rv[val.store_id] = rv[val.store_id] || []).push(val)
        return rv
      }, {})
    })
  }

  ngOnInit(): void {
    this.listenToFormChanges()
    // Preload stores and service products once
    // The parent component already has the period data and passes it via @Input
    // We don't need to subscribe to auth changes - the modal gets its data from the parent
    this._preload()
    this._setupSellerSearch()
  }

  ngOnDestroy(): void {
    this.sellersInput$.complete()
    this.sellers$.complete()
  }

  onChange(): void {
    if (this._open == true) {
      if (this._modal) {
        // already open - if period was null before but is now set, initialize form
        if (this.editingPeriod && !this.salesPeriodForm.get('store_id').value) {
          console.log('[SalesPeriodEditModal] Modal already open, initializing form with period')
          this.initializeSalesPeriodForm()
        }
      } else {
        // Opening modal - the period should always be available from the parent component
        // since it's already loaded when the view is displayed
        if (!this.editingPeriod && !this._selectionData) {
          // This should not happen in normal operation - period is always loaded in the view
          // If it does happen, it's likely a bug or the period hasn't been passed via @Input yet
          console.warn('[SalesPeriodEditModal] Opening modal without period or selection data - period should be available from parent')
          // Wait briefly for @Input setter to be called, then initialize empty form as fallback
        } else if (this._selectionData) {
          // Selection data is available, form will be initialized by _updateFormFromSelection
        } else if (this.editingPeriod) {
          // Period is available - initialize form immediately
          // This ensures instant display with no delay
          this.initializeSalesPeriodForm()
        }
        // Note: If period or selection are set, initializeSalesPeriodForm() 
        // is already called by _updateFormFromPeriod() or _updateFormFromSelection()
        // Don't call it again here as it would reset the preselected values.
        this._modal = this.modalService.open(this.modalTemplate, { 
          size: 'lg', 
          centered: true, 
          ariaLabelledBy: 'modal-basic-title',
          backdrop: true,
          keyboard: true,
          container: 'body',
          windowClass: 'sales-period-modal'
        })
        this._modal.result.then(
          closingReason => { this.close() },
          dismissalReason => { this.close() })
        
        // If period wasn't initialized before modal opened, try once more after modal is shown
        // This handles the edge case where @Input setter hasn't been called yet
        this._modal.shown.pipe(take(1)).subscribe(() => {
          if (this.editingPeriod && !this.salesPeriodForm.get('store_id').value) {
            // Period is now available, initialize form
            console.log('[SalesPeriodEditModal] Period available after modal opened, initializing form')
            this.initializeSalesPeriodForm()
          } else if (!this.editingPeriod && !this._selectionData) {
            // Still no period - this shouldn't happen, but initialize empty form as fallback
            console.warn('[SalesPeriodEditModal] No period available after modal opened - this should not happen')
            this.initializeSalesPeriodForm()
          }
          
          // Focus the seller dropdown after the modal is fully shown
          setTimeout(() => {
            const sellerInput = document.querySelector('.sales-period-modal ng-select[formcontrolname="seller_id"] input')
            if (sellerInput) {
              (sellerInput as HTMLElement).focus()
            }
          }, 100)
        })
      }
    } else {
      // close modal
      if (!this._modal) {
        // already closed
      } else {
        this._modal.close()
      }
    }
  }

  onDatePickerNavigate(event): void {
    var selectedRackId = +this.salesPeriodForm.get('rack_id').value
    if (!selectedRackId) {
      this.availabilityOfSelectedRack = [] // availability not known
    } else {
      this.racksService.getAvailability(selectedRackId, event.next.year, event.next.month).subscribe(
        availability => {
          this.availabilityOfSelectedRack = availability.available
        }
      )
    }
  }

  customPeriodStatusesEnabled(): boolean {
    // Use optional chaining for safe access during token refresh
    const merchant = this.auth.currentUserValue?.merchant
    return merchant?.custom_period_statuses?.length > 0 || false
  }

  getServiceProductVariations(serviceProductId: number): ServiceProductVariation[] {
    const sp = this._productForId(serviceProductId)
    return sp ? sp.service_product_variations.sort((a,b) => { return a.name ? a.name.localeCompare(b.name) : 0 }) : null
  }

  private _productForId(id: number): ServiceProduct {
    return this.serviceProducts ? this.serviceProducts.find(sp => { return sp.id == id }) : null
  }

  private _storeForId(id: number): Store {
    return this.stores ? this.stores.find(store => { return store.id == id }) : null
  }

  private _updateServiceProductsForSelectedStore(storeId: number) {
    this.serviceProductsForSelectedStore = this.storesService.filterProductsForStore(this.serviceProducts, storeId)

    let f = this.salesPeriodForm
    let productId = f.get('service_product_id').value
    if (!this._isSelectableProductId(productId)) {
      f.get('service_product_id').setValue('')
    }
  }

  private _updateRacksForSelectedStore(storeId: number) {
    this.racksForSelectedStore = this.storesService.filterRacksForStore(this.serviceProducts, storeId)

    let f = this.salesPeriodForm
    let rackId = +f.get('rack_id').value
    if (!this._isSelectableRackId(rackId)) {
      f.get('rack_id').setValue('')
    }

    // Update racks for both store and product
    this._updateRacksForSelectedStoreAndProduct(storeId, +f.get('service_product_id').value)
  }

  private _updateRacksForSelectedStoreAndProduct(storeId: number, serviceProductId: number) {
    const store = this._storeForId(storeId)
    const product = this._productForId(serviceProductId)
    if (!store || !product) {
      this.racksForSelectedStoreAndProduct = null
      return
    }
    this.racksForSelectedStoreAndProduct = product.recks
      .filter(r => { return r.store_id == store.id })
      .sort((a,b) => { return a.name ? a.name.localeCompare(b.name) : 0 })
  }

  private _isSelectableRackId(rackId: number) {
    return this.racksForSelectedStore.filter(r => { return r.id == rackId }).length > 0
  }

  private _isSelectableProductId(productId: number): boolean {
    let x = this.serviceProductsForSelectedStore.filter(p => { return p.id == productId }).length > 0
    return x
  }

  private _updateDataSourcesForEditingPeriod(): void {
    // This method is called after serviceProducts are loaded to update data sources for editing period
    if (this.editingPeriod && this.editingPeriod.store && this.editingPeriod.store.id) {
      this._updateServiceProductsForSelectedStore(this.editingPeriod.store.id)
      this._updateRacksForSelectedStore(this.editingPeriod.store.id)
      if (this.editingPeriod.service_product && this.editingPeriod.service_product.id) {
        this._updateRacksForSelectedStoreAndProduct(this.editingPeriod.store.id, this.editingPeriod.service_product.id)
      }
    }
  }

  onSubmitSalesPeriod() {
    if (this.submitting) {
      return
    }
    this.submitting = true
    let f = this.salesPeriodForm
    f.get('from').enable()
    f.get('to').enable()
    const create = !this.editingPeriod
    this.salesPeriodsService
      .saveOrCreate(this.salesPeriodForm.value, create ? null : this.editingPeriod.id)
      .subscribe(
        period => {
          this.submitting = false
          this.close()
          if (create) {
            this.created.emit(period)
            // nb do not set to this.period
          } else {
            this.editingPeriod = period
            this.periodChange.emit(this.editingPeriod)
          }
        },
        error => {
          this.submitting = false
          let msg = error.message + (error.errors ? (': ' + error.errors.join('; ')) : '')
          this.alertService.error(this.translate.instant(msg))
        }
      )
  }

  periodLinkHref(period: SalesPeriod): string {
    return `/period/${this.editingPeriod.id}`
  }

  public close(): void {
    if (this._modal) {
      this._modal.close()
    }
    this._modal = null
    this._selectionData = null // Clear selection data for next time
    // Don't clear editingPeriod here - keep it so it's available when reopening
    // The period is still valid in the parent component, so we should preserve it
    // Only clear it if we're actually switching to a different period or creating a new one
    // this.editingPeriod = null // Clear editing period for next time
    this.closed.emit(true) // true = did close
  }

  isPaid(): boolean {
    return this.editingPeriod && this.editingPeriod.payment_status === 'payment_status_paid'
  }

  cancelSalePeriodEdit(): void {
    this.close()
  }

  toggle(datepicker): void {
    datepicker.toggle()
    this.datePickerOpen = !this.datePickerOpen
  }

  onDatePickerClosed(event): void {
    this.datePickerOpen = false
    if (!this.selectionToDate) {
      if (!this.selectionFromDate) {
        this.selectionFromDate = this.asNgbDate(startOfTomorrow())
      }
      this.selectionToDate = this.selectionFromDate
    }
    let f = this.salesPeriodForm
    f.get('from').setValue(formatDate(this.ngbToDate(this.selectionFromDate), 'yyyy-MM-dd', 'en'))
    f.get('to').setValue(formatDate(this.ngbToDate(this.selectionToDate), 'yyyy-MM-dd', 'en'))
  }

  // start: datepicker code from online (modified)
  hoveredDate: NgbDate | null = null
  selectionFromDate: NgbDate | null
  selectionToDate: NgbDate | null
  onDateSelection(date: NgbDate) {
    if (!this.selectionFromDate && !this.selectionToDate) {
      this.selectionFromDate = date
    } else if (this.selectionFromDate && !this.selectionToDate && date && date.after(this.selectionFromDate)) {
      this.selectionToDate = date
    } else {
      this.selectionToDate = null
      this.selectionFromDate = date
    }
  }
  isHovered(date: NgbDate): boolean {
    return this.selectionFromDate && !this.selectionToDate && this.hoveredDate && date.after(this.selectionFromDate) && date.before(this.hoveredDate)
  }
  isInside(date: NgbDate): boolean {
    return this.selectionToDate && date.after(this.selectionFromDate) && date.before(this.selectionToDate)
  }
  isRange(date: NgbDate): boolean {
    return date.equals(this.selectionFromDate) || (this.selectionToDate && date.equals(this.selectionToDate)) || this.isInside(date) || this.isHovered(date)
  }
  isUnavailable(date: NgbDate): boolean {
    const avail = this.isAvailable(date)
    return !avail
  }
  isAvailable(date: NgbDate): boolean {
    if (!this.availabilityOfSelectedRack || this.availabilityOfSelectedRack.length == 0) {
      return true
    }
    return !!this.availabilityOfSelectedRack.find(a => {
      return date.year == a.year() && date.month == a.monthValue() && date.day == a.dayOfMonth()
    })
  }
  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input)
    const result = parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue
    return result
  }
  // datepicker stuff end

  ngbToDate(ngbDate: NgbDate): Date {
    if (!ngbDate) {
      return null
    }
    // HACK set the hours 12 midday to avoid UTC-vs-EET fights
    // TODO fix this hack so that this app works globally
    return new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day, 12, 0, 0, 0)
  }

  get selectedSellerId(): number {
    let id = this.salesPeriodForm.get('seller_id').value
    return id ? +id : undefined
  }

  isSellerImmutable(): boolean {
    // Seller is immutable when editing an existing period that already has a seller
    return this.editingPeriod != null && this.editingPeriod.seller_id != null
  }


  private asNgbDate(d: Date): NgbDate {
    return d == null ? null : new NgbDate(d.getFullYear(), d.getMonth() + 1, d.getDate())
  }

  customPeriodStatuses(): CustomPeriodStatus[] {
    // Use optional chaining for safe access during token refresh
    const statuses = this.auth.currentUserValue?.merchant?.custom_period_statuses
    if (!statuses || statuses.length === 0) {
      return []
    }
    return statuses.sort((a,b) => { return a.order - b.order })
  }

  classForPaymentStatusRadioElement(status: string): string {
    return this.salesPeriodForm.get('payment_status').value == status ? 'badge-success' : 'badge-primary'
  }

  classForPaymentStatus(paymentStatus: string): string {
    switch (paymentStatus) {
      case 'payment_status_paid': return 'btn-success'
      case 'payment_status_pending': return 'btn-warning'
      case 'payment_status_error': return 'btn-warning'
      default: return 'btn-light'
    }
  }

  styleForCustomStatus(status: CustomPeriodStatus): any {
    if (this.isCustomStatusSelected(status)) {
      return { 'background-color': status.color, 'color': 'black' }
    } else {
      return {}
    }
  }

  classForCustomStatus(status: CustomPeriodStatus): any {
    if (this.isCustomStatusSelected(status)) {
      return ''
    } else {
      return 'badge-light'
    }
  }

  isCustomStatusSelected(status: CustomPeriodStatus): boolean {
    var sel = this.salesPeriodForm.get('custom_period_status_id').value
    return sel == status.id
  }

  customStatusSelected(status: CustomPeriodStatus): void {
    const currentValue = this.salesPeriodForm.get('custom_period_status_id').value
    
    // If clicking the same status that's already selected, deselect it
    if (currentValue === status.id.toString()) {
      this.salesPeriodForm.get('custom_period_status_id').setValue('')
    } else {
      // Otherwise, select the new status
      this.salesPeriodForm.get('custom_period_status_id').setValue(status.id.toString())
    }
  }

  private listenToFormChanges() {
    let f = this.salesPeriodForm
    f.get('store_id').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      let storeId = +value
      this._updateServiceProductsForSelectedStore(storeId)
      this._updateRacksForSelectedStore(storeId)
    })
    f.get('service_product_id').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      f.get('service_product_variation_id').setValue(null)
      f.get('rack_id').setValue(null)
      // Update racks for the newly selected product
      this._updateRacksForSelectedStoreAndProduct(+f.get('store_id').value, +value)
    })
    f.get('seller_id').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      // Clear sellers$ when seller is changed or cleared to avoid stale data
      if (!value) {
        this.sellers$.next([])
      }
    })
  }

  private _preload(): void {
    // Load stores from the current user if available
    // Use optional chaining for safety during token refresh
    const stores = this.auth.currentUserValue?.merchant?.stores
    this.stores = stores || []

    // Load service products if not already loaded
    if (this.serviceProducts === null) {
      this.serviceProductService.serviceProducts
        .subscribe(s => {
          this.serviceProducts = s
          this.serviceProducts.sort((a, b) => { return a.code.localeCompare(b.code) })
          // Update data sources if we're editing a period
          this._updateDataSourcesForEditingPeriod()
        })
    }
  }

  private _setupSellerSearch(): void {
    this.sellersInput$.pipe(
      startWith(''), // Start with empty string
      debounceTime(300),
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe(term => {
      // If no term or term is too short, clear results
      if (!term || term.length < this.minSearchLength) {
        this.sellersLoading = false;
        this.sellers$.next([]);
        return;
      }
      
      // Otherwise search for sellers
      this.sellersLoading = true;
      this.sellersService.searchSellers(term).subscribe({
        next: (results) => {
          this.sellersLoading = false;
          this.sellers$.next(results); // Manually set results
        },
        error: () => {
          this.sellersLoading = false;
          this.sellers$.next([]); // Clear on error
        }
      });
    });
  }

  private _updateFormFromSelection(selection: SelectedVariationAndDate): void {
    if (!selection) {
      return
    }

    let store_id = selection.rack.store_id
    let product_id = selection.product.id
    let variation_id = selection.variation.id
    let rack_id = selection.rack.id
    let from = selection.from
    let to = selection.to

    let price = selection.variation.price
    let commission = selection.variation.commission || 0

    this.initializeSalesPeriodForm()

    // If serviceProducts aren't loaded yet, wait for them
    if (!this.serviceProducts) {
      // Subscribe to get the products, then update the form
      this.serviceProductService.serviceProducts.pipe(untilDestroyed(this)).subscribe(products => {
        if (products && products.length > 0) {
          this._applySelectionToForm(store_id, product_id, variation_id, rack_id, price, commission, from, to)
        }
      })
    } else {
      this._applySelectionToForm(store_id, product_id, variation_id, rack_id, price, commission, from, to)
    }
  }
  
  private _applySelectionToForm(store_id: number, product_id: number, variation_id: number, rack_id: number, price: number, commission: number, from: any, to: any): void {
    // First update JUST the dropdown options without validating current form values
    // (because we're about to set new ones)
    this.serviceProductsForSelectedStore = this.storesService.filterProductsForStore(this.serviceProducts, store_id)
    this.racksForSelectedStore = this.storesService.filterRacksForStore(this.serviceProducts, store_id)
    this._updateRacksForSelectedStoreAndProduct(store_id, product_id)
    
    // The variation and rack ng-selects are conditionally rendered with 
    // *ngIf="salesPeriodForm.controls['service_product_id'].value"
    // So we need to set values in stages to ensure the components exist
    
    setTimeout(() => {
      // Stage 1: Set store and service product first (without emitting events)
      this.salesPeriodForm.patchValue({
        store_id: store_id,
        service_product_id: product_id,
        price: price.toString(),
        commission: commission.toString()
      }, { emitEvent: false })
      
      // Stage 2: Wait for Angular to render the conditional ng-selects, then set variation and rack
      setTimeout(() => {
        this.salesPeriodForm.patchValue({
          service_product_variation_id: variation_id,
          rack_id: rack_id
        }, { emitEvent: false })
        
        // Set dates
        let fromDate = from.asDate()
        let toDate = to.asDate()

        this.salesPeriodForm.get('from').setValue(formatISO(fromDate, { representation: 'date' }))
        this.salesPeriodForm.get('to').setValue(formatISO(toDate, { representation: 'date' }))
        this.selectionFromDate = this.asNgbDate(fromDate)
        this.selectionToDate = this.asNgbDate(toDate)
      }, 0)
    }, 0)
  }

  private _updateFormFromPeriod(period: SalesPeriod) {
    // Only update if period actually changed (by reference or ID)
    const periodChanged = !this.editingPeriod || 
                          !period || 
                          this.editingPeriod.id !== period.id
    
    this.editingPeriod = period
    if (!this.editingPeriod) {
      // Period is null - this should not happen in normal operation
      // The period is always loaded in the parent component and should be available
      // Token refresh does not affect loaded data, so period should never be null
      if (this._open && this._modal) {
        console.warn('[SalesPeriodEditModal] Period is null but modal is open - this should not happen')
      }
      return
    }
    // Only initialize if modal is open or about to open
    // This prevents initializing with stale data before modal opens
    if (this._open || this._modal) {
      // Check if form is already initialized (has store_id value)
      // This prevents re-initializing if form was already set up and period hasn't changed
      const formAlreadyInitialized = this.salesPeriodForm.get('store_id').value
      
      // If modal is already open, initialize immediately if not already initialized or period changed
      // Otherwise, it will be initialized when modal opens
      if (this._modal) {
        if (!formAlreadyInitialized || periodChanged) {
          console.log('[SalesPeriodEditModal] Period set, initializing form', { 
            periodId: period.id, 
            formInitialized: formAlreadyInitialized,
            periodChanged 
          })
          this.initializeSalesPeriodForm()
        }
      } else {
        // Modal not open yet, will initialize when it opens
        // But we can pre-initialize if we're about to open
        if (this._open) {
          this.initializeSalesPeriodForm()
        }
      }
    }
  }

  private initializeSalesPeriodForm(): void {
    const period = this.editingPeriod
    if (!period) { // creating
      this.salesPeriodForm.setValue({
        store_id: this.stores && this.stores.length > 0 ? (this.stores[0].id as any) : '',
        service_product_id: '',
        service_product_variation_id: '',
        rack_id: '',
        rack_time_slot_id: '',
        from: '',
        to: '',
        price: '',
        commission: '',
        max_items: '',
        discount: '',
        seller_id: '',
        seller_note: '',
        internal_note: '',
        payment_status: 'payment_status_none',
        custom_period_status_id: ''
      })
    } else {
      // Update dropdown options first
      if (period.store && period.store.id && this.serviceProducts) {
        this.serviceProductsForSelectedStore = this.storesService.filterProductsForStore(this.serviceProducts, period.store.id)
        this.racksForSelectedStore = this.storesService.filterRacksForStore(this.serviceProducts, period.store.id)
        if (period.service_product && period.service_product.id) {
          this._updateRacksForSelectedStoreAndProduct(period.store.id, period.service_product.id)
        }
      }
      
      // Populate sellers$ with the seller from the period so it displays in ng-select
      if (period.seller) {
        this.sellers$.next([period.seller])
      }
      
      // Two-stage form value setting (same as creation)
      // Stage 1: Set store and product first to trigger conditional ng-selects to render
      this.salesPeriodForm.patchValue({
        store_id: period.store.id,
        service_product_id: period.service_product.id,
        price: period.price,
        commission: period.commission,
        max_items: period.capacity || 0,
        discount: period.discount,
        seller_id: period.seller_id,
        seller_note: period.seller_note,
        internal_note: period.internal_note || '',
        payment_status: period.payment_status,
        rack_time_slot_id: period.rack_time_slot_id,
        from: period.rack_time_slot.from,
        to: period.rack_time_slot.to,
        custom_period_status_id: period.custom_period_status_id || ''
      }, { emitEvent: false })
      
      // Stage 2: Wait for Angular to render conditional ng-selects, then set variation and rack
      setTimeout(() => {
        this.salesPeriodForm.patchValue({
          service_product_variation_id: period.service_product_variation_id,
          rack_id: period.rack_time_slot.reck_id
        }, { emitEvent: false })
      }, 0)
      
      this.selectionFromDate = this.asNgbDate(new Date(period.rack_time_slot.from))
      this.selectionToDate = this.asNgbDate(new Date(period.rack_time_slot.to))

      if (this.editingPeriod.payment_status === 'payment_status_paid') {
        this.salesPeriodForm.controls['price'].disable()
      }
    }
    // cannot manually edit these; only via datepicker
    this.salesPeriodForm.controls['from'].disable()
    this.salesPeriodForm.controls['to'].disable()
  }
}
